'use client';

import React, { FC, ReactNode, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import classnames from 'classnames';
import { CURRENCIES, Currency, CurrencyKey, CrossRateTabs, Trend } from './config';
import { generateStockLinkBySymbol } from '@/utils/links';
import { PUBLIC } from '@/constants/image/path';
import useFixedCard from '@/store/useFixedCard';

const tabToCurrencyKey: Record<CrossRateTabs, CurrencyKey> = {
  [CrossRateTabs.PRICE]: CurrencyKey.PRICE,
  [CrossRateTabs.HEAT]: CurrencyKey.HEAT
};

interface TableCellProperty {
  className?: string;
  currency?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children?: ReactNode;
}

const TableCell: FC<TableCellProperty> = ({ children, className, onClick, onMouseEnter, onMouseLeave }) => {
  return (
    <td
      className={classnames(
        'h-[38px] min-w-[68px] max-w-[68px]',
        'rounded-md border border-[#fff]',
        'text-sm/[1] text-[#383838]',
        className
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      suppressHydrationWarning
    >
      {children}
    </td>
  );
};

const TableCellWithIcon: FC<TableCellProperty> = ({ className, currency }) => {
  return (
    <TableCell className={classnames('bg-[#f6f8fc]', 'font-bold text-[#000]', className)}>
      <div className={classnames('h-full', 'flex items-center justify-center')}>
        <Image
          className='mr-[5px]'
          src={`${PUBLIC}/icons/countries/${currency}.svg`}
          height={16}
          width={16}
          alt='country-icon'
        />
        {currency}
      </div>
    </TableCell>
  );
};

interface TableProperty {
  isDesktop?: boolean;
  tab: CrossRateTabs;
  data: (Currency | null)[][];
}

const Table: FC<TableProperty> = ({ isDesktop, tab, data }) => {
  const { setSymbol, setComponent } = useFixedCard();
  const router = useRouter();
  const [onHover, setOnHover] = useState<Record<string, number | null>>({ row: null, col: null });

  const tableCellonClick = (cell: Currency | null) => () => {
    if (typeof cell?.[CurrencyKey.SYMBOL] !== 'string') return;
    if (isDesktop) {
      router.push(generateStockLinkBySymbol(cell?.[CurrencyKey.SYMBOL]));
    } else {
      setSymbol(cell?.[CurrencyKey.SYMBOL]);
      setComponent('RateDetails');
    }
  };

  const tableCellonMouseEnter = (i: number, j: number) => () => {
    setOnHover({ row: j, col: i });
  };

  const tableCellonMouseLeave = () => {
    setOnHover({ row: null, col: null });
  };

  return (
    <div className={classnames('flex bg-white', 'md:bg-transparent')}>
      <div className={classnames('shadow-cross-rate-table', 'md:shadow-none')}>
        <table className={classnames('my-3 ml-3 mr-[1px]', 'md:mb-8 md:ml-4 md:mr-0 md:mt-0')}>
          <tbody>
            <tr>
              <TableCell className={classnames('bg-[#f6f8fc]')} />
            </tr>
            {CURRENCIES?.map((currency, index) => (
              <tr key={currency}>
                <TableCellWithIcon
                  className={classnames({ '!bg-[#54b5c6] !text-white': onHover?.col === index })}
                  currency={currency}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={classnames('overflow-x-auto')}>
        <table className={classnames('my-3 table-fixed', 'md:mb-8 md:mt-0 md:table-auto')}>
          <tbody>
            <tr>
              {CURRENCIES?.map((currency, index) => (
                <TableCellWithIcon
                  key={currency}
                  className={classnames(
                    { 'ml-[70px]': index === 0 },
                    { '!bg-[#54b5c6] text-white': onHover?.row === index }
                  )}
                  currency={currency}
                />
              ))}
            </tr>
            {data?.map((row, i) => (
              <tr key={CURRENCIES[i]}>
                {row?.map((cell, j) => (
                  <TableCell
                    key={CURRENCIES?.[j]}
                    className={classnames(
                      'text-center',
                      { 'cursor-pointer hover:bg-[#54b5c6] hover:text-white': cell?.[tabToCurrencyKey?.[tab]] },
                      { 'bg-[#f6f8fc]': cell?.[CurrencyKey.DIRECTION] === Trend.UNCHANGED },
                      { 'bg-rising': cell?.[CurrencyKey.DIRECTION] === Trend.RISING },
                      { 'bg-falling': cell?.[CurrencyKey.DIRECTION] === Trend.FALLING }
                    )}
                    onClick={tableCellonClick(cell)}
                    onMouseEnter={tableCellonMouseEnter(i, j)}
                    onMouseLeave={tableCellonMouseLeave}
                  >
                    {cell?.[tabToCurrencyKey?.[tab]]}
                    {cell?.[tabToCurrencyKey?.[tab]] && tab === CrossRateTabs.HEAT && '%'}
                  </TableCell>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
